import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
  FormHelperText,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
  Info as InfoIcon,
  Factory as FactoryIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import DeleteIcon from "@/assets/svgs/garbage.svg";
import {
  FormRule,
  getAuthorityStyle,
  getRuleSummary,
  RateType,
  ValueBasis,
  ExclusiveMode,
  AUTHORITIES,
} from './helpers';
import {
  DatePicker,
  TextInput,
  MultiAutocomplete,
} from '@/components';
import AuthorityCheckboxGroup from '../authority-checkbox-group';
import VirtualizedChipList from '@/components/virtualized-chip-list';
import { tokens } from "@/tokens";

interface DutyRuleFormSectionProps {
  rule: FormRule;
  index: number;
  updateRule: (index: number, field: keyof FormRule, value: any) => void;
  removeRule: (index: number) => void;
  duplicateRule: (index: number) => void;
  toggleRuleExpanded: (index: number) => void;
  formErrors: Record<string, string>;
}

const DutyRuleFormSection: React.FC<DutyRuleFormSectionProps> = ({
  rule,
  index,
  updateRule,
  removeRule,
  duplicateRule,
  toggleRuleExpanded,
  formErrors,
}) => {
  const [htsInputValue, setHtsInputValue] = useState('');
  const [exclusionsInputValue, setExclusionsInputValue] = useState('');

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: rule.isExpanded ? tokens.bg.hover : '#ffffff',
        border: rule.isExpanded ? '2px solid #3b82f6' : '1px solid #e2e8f0',
        borderRadius: tokens.radius.lg,
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: rule.isExpanded ? '#3b82f6' : '#cbd5e1',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      {/* Collapsed Header */}
      <Box
        sx={{
          p: 2.5,
          cursor: 'pointer',
          backgroundColor: rule.isExpanded ? tokens.bg.hover : 'transparent',
          '&:hover': {
            backgroundColor: rule.isExpanded ? '#f1f5f9' : tokens.bg.hover,
          },
        }}
        onClick={() => toggleRuleExpanded(index)}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
            <IconButton
              size="small"
              sx={{
                transform: rule.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
                color: '#3b82f6',
              }}
            >
              <ExpandMoreIcon />
            </IconButton>

            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={tokens.fontWeight.bold} sx={{ color: tokens.text.primary }}>
                Rule {index + 1}
              </Typography>
              {rule.authority && (
                <Chip
                  label={rule.authority}
                  size="small"
                  sx={{
                    backgroundColor: getAuthorityStyle(rule.authority).color as any,
                    color: 'white',
                    fontWeight: tokens.fontWeight.bold,
                  }}
                />
              )}
              {rule.isMetal && (
                <Chip
                  icon={<FactoryIcon sx={{ fontSize: 16 }} />}
                  label="Metal %"
                  size="small"
                  variant="outlined"
                  color="warning"
                />
              )}
            </Stack>

            {!rule.isExpanded && (
              <Typography
                variant="body2"
                sx={{
                  color: tokens.text.secondary,
                  ml: 2,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {getRuleSummary(rule)}
              </Typography>
            )}
          </Stack>

          <Stack direction="row" alignItems="center">
            <Tooltip title="Duplicate Rule">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  duplicateRule(index);
                }}
                sx={{
                  color: '#475569',
                  '&:hover': {
                    backgroundColor: '#e2e8f0',
                  },
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Rule">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  removeRule(index);
                }}
                sx={{
                  color: tokens.status.danger,
                  '&:hover': {
                    backgroundColor: tokens.status.dangerBg,
                  },
                }}
              >
                <img src={DeleteIcon} alt="Delete" style={{ width: 20, height: 20 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      {/* Expanded Content */}
      <Collapse in={rule.isExpanded}>
        <Box sx={{ p: 3, pt: 0 }}>
          {/* Basic Rule Fields */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <TextInput
                label="Priority"
                value={rule.priority.toString()}
                handleChangeValue={(value) => updateRule(index, 'priority', Number(value))}
                type="number"
                InputProps={{
                  inputProps: { min: 1 }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <TextInput
                label="Rate (%)"
                value={rule.rate}
                handleChangeValue={(value) => updateRule(index, 'rate', value)}
                error={!!formErrors[`rule_${index}_rate`]}
                helperText={formErrors[`rule_${index}_rate`]}
                type="number"
                InputProps={{
                  inputProps: { step: 0.1 }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Authority</InputLabel>
                <Select
                  value={rule.authority || ''}
                  onChange={(e) => updateRule(index, 'authority', e.target.value)}
                  label="Authority"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {AUTHORITIES.map((auth: any) => (
                    <MenuItem key={auth} value={auth}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getAuthorityStyle(auth).icon}
                        <span>{auth}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Legal authority for this duty</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>

          {/* Countries Section */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{
                p: 2,
                backgroundColor: tokens.bg.hover,
                borderRadius: tokens.radius.md,
                border: '1px solid #e2e8f0'
              }}>
                <Typography variant="subtitle2" fontWeight={tokens.fontWeight.bold} sx={{ mb: 1, color: tokens.text.body }}>
                  Countries (Applies To)
                </Typography>
                <MultiAutocomplete
                  value={rule.countries}
                  onChange={(value) => updateRule(index, 'countries', value)}
                  label="Countries to Include"
                  placeholder="Enter country codes (e.g., CN, US, VN)"
                  helperText="Leave empty for all countries"
                  normalizeValue={(value: any) => {
                    return typeof value === 'string' ? value.toUpperCase().trim() : value;
                  }}
                  chipSx={{ backgroundColor: 'white' }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{
                p: 2,
                backgroundColor: tokens.bg.hover,
                borderRadius: tokens.radius.md,
                border: '1px solid #e2e8f0'
              }}>
                <Typography variant="subtitle2" fontWeight={tokens.fontWeight.bold} sx={{ mb: 1, color: tokens.text.body }}>
                  Country Exclusions
                </Typography>
                <MultiAutocomplete
                  value={rule.countryExclusions || []}
                  onChange={(value) => updateRule(index, 'countryExclusions', value)}
                  label="Countries to Exclude"
                  placeholder="Enter country codes to exclude"
                  helperText="These countries will be exempt from this rule"
                  normalizeValue={(value: any) => {
                    return typeof value === 'string' ? value.toUpperCase().trim() : value;
                  }}
                  chipSx={{ backgroundColor: 'white' }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* HTS Codes Section */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{
                p: 2,
                backgroundColor: tokens.bg.hover,
                borderRadius: tokens.radius.md,
                border: '1px solid #e2e8f0'
              }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={tokens.fontWeight.bold} sx={{ color: tokens.text.body }}>
                    HTS Codes (Includes)
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<ClearIcon />}
                    onClick={() => updateRule(index, 'htsCodes', [])}
                    disabled={rule.htsCodes.length === 0}
                    sx={{ color: tokens.text.secondary }}
                  >
                    Clear All
                  </Button>
                </Stack>
                <MultiAutocomplete
                  value={[]}
                  inputValue={htsInputValue}
                  onInputChange={(_, newValue) => setHtsInputValue(newValue)}
                  onChange={(newValue) => {
                      const existingCodes = new Set(rule.htsCodes);
                      const newCodes = newValue.filter((v: string) => !existingCodes.has(v));
                      if (newCodes.length > 0) {
                          updateRule(index, 'htsCodes', [...rule.htsCodes, ...newCodes]);
                      }
                  }}
                  label="HTS Codes to Include"
                  placeholder="Enter or paste HTS codes"
                  onPaste={(event: React.ClipboardEvent<HTMLDivElement>) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const pastedText = event.clipboardData.getData('text');
                    const codes = pastedText
                      .split(/[\s,\n\r\t;]+/)
                      .map(code => code.trim().replace(/\D/g, ""))
                      .filter(code => code && code.length >= 2 && code.length <= 10);
                    
                    if (codes.length > 0) {
                      const existingSet = new Set(rule.htsCodes);
                      const newCodes = codes.filter(code => !existingSet.has(code));
                      if (newCodes.length > 0) {
                        updateRule(index, 'htsCodes', [...rule.htsCodes, ...newCodes]);
                      }
                      setTimeout(() => setHtsInputValue(''), 50);
                    }
                  }}
                  renderTags={() => null} 
                />
                <Box sx={{ mt: 1, border: `1px solid ${tokens.border.default}`, borderRadius: '4px', overflow: 'hidden' }}>
                  <VirtualizedChipList
                      key={`hts-${index}-${rule.htsCodes.length}`}
                      items={rule.htsCodes}
                      onDeleteItem={(codeToDelete: any) => {
                          const newCodes = rule.htsCodes.filter((c: any) => c !== codeToDelete);
                          updateRule(index, 'htsCodes', newCodes);
                      }}
                  />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{
                p: 2,
                backgroundColor: tokens.bg.hover,
                borderRadius: tokens.radius.md,
                border: '1px solid #e2e8f0'
              }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={tokens.fontWeight.bold} sx={{ color: tokens.text.body }}>
                    Exclusions (Override)
                  </Typography>
                    <Button
                    size="small"
                    variant="text"
                    startIcon={<ClearIcon />}
                    onClick={() => updateRule(index, 'exclusions', [])}
                    disabled={!rule.exclusions || rule.exclusions.length === 0}
                    sx={{ color: tokens.text.secondary }}
                  >
                    Clear All
                  </Button>
                </Stack>
                  <MultiAutocomplete
                      value={[]}
                      inputValue={exclusionsInputValue}
                      onInputChange={(_, newValue) => setExclusionsInputValue(newValue)}
                      onChange={(newValue) => {
                          const existingCodes = new Set(rule.exclusions || []);
                          const newCodes = newValue.filter((v: string) => !existingCodes.has(v));
                          if (newCodes.length > 0) {
                              updateRule(index, 'exclusions', [...(rule.exclusions || []), ...newCodes]);
                          }
                      }}
                      label="HTS Codes to Exclude"
                      placeholder="Enter or paste HTS codes"
                      onPaste={(event: React.ClipboardEvent<HTMLDivElement>) => {
                        event.preventDefault();
                        event.stopPropagation();
                        const pastedText = event.clipboardData.getData('text');
                        const codes = pastedText
                          .split(/[\s,\n\r\t;]+/)
                          .map(code => code.trim().replace(/\D/g, ""))
                          .filter(code => code && code.length >= 2 && code.length <= 10);
                        
                        if (codes.length > 0) {
                          const existingSet = new Set(rule.exclusions || []);
                          const newCodes = codes.filter(code => !existingSet.has(code));
                          if (newCodes.length > 0) {
                            updateRule(index, 'exclusions', [...(rule.exclusions || []), ...newCodes]);
                          }
                          setTimeout(() => setExclusionsInputValue(''), 50);
                        }
                      }}
                      renderTags={() => null}
                  />
                    <Box sx={{ mt: 1, border: `1px solid ${tokens.border.default}`, borderRadius: '4px', overflow: 'hidden' }}>
                      <VirtualizedChipList
                          key={`exclusions-${index}-${(rule.exclusions || []).length}`}
                          items={rule.exclusions || []}
                          onDeleteItem={(codeToDelete: any) => {
                              const newCodes = (rule.exclusions || []).filter((c: any) => c !== codeToDelete);
                              updateRule(index, 'exclusions', newCodes);
                          }}
                      />
                  </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Date Range */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Start Date"
                value={rule.startDate}
                onChangeValue={(date) => updateRule(index, 'startDate', date || '')}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <DatePicker
                label="End Date (Optional)"
                value={rule.endDate || null}
                onChangeValue={(date) => updateRule(index, 'endDate', date || '')}
              />
            </Grid>
          </Grid>

          {/* Advanced Configuration */}
          <Divider sx={{ my: 3 }}>
            <Chip label="Advanced Configuration" size="small" />
          </Divider>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Rate Type</InputLabel>
                    <Select
                      value={rule.rateType || RateType.ADVALOREM}
                      onChange={(e) => updateRule(index, 'rateType', e.target.value)}
                      label="Rate Type"
                    >
                      <MenuItem value={RateType.ADVALOREM}>Ad Valorem (%)</MenuItem>
                      <MenuItem value={RateType.SPECIFIC}>Specific ($/unit)</MenuItem>
                      <MenuItem value={RateType.REPLACEMENT}>Replacement</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Value Basis</InputLabel>
                    <Select
                      value={rule.valueBasis || ValueBasis.ENTERED_VALUE}
                      onChange={(e) => updateRule(index, 'valueBasis', e.target.value)}
                      label="Value Basis"
                    >
                      <MenuItem value={ValueBasis.ENTERED_VALUE}>Entered Value</MenuItem>
                      <MenuItem value={ValueBasis.STEEL_PORTION}>Steel Portion</MenuItem>
                      <MenuItem value={ValueBasis.ALUMINUM_PORTION}>Aluminum Portion</MenuItem>
                      <MenuItem value={ValueBasis.QUOTA_TIER}>Quota Tier</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={rule.isMetal || false}
                        onChange={(e) => updateRule(index, 'isMetal', e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>Metal Adjustment</span>
                        <Tooltip title="Rate will be adjusted by product's metal percentage">
                          <InfoIcon fontSize="small" />
                        </Tooltip>
                      </Box>
                    }
                  />
                </Grid>

                {/* Ceiling Controls */}
                <Grid item xs={12} md={4}>
                  <TextInput
                    label="Ceiling Percent (%)"
                    type="number"
                    value={typeof rule.ceilingPercent === 'number' ? (rule.ceilingPercent * 100).toString() : ''}
                    handleChangeValue={(value) => {
                      const num = value === '' ? undefined : Number(value) / 100;
                      updateRule(index, 'ceilingPercent' as any, num);
                    }}
                    placeholder="e.g., 15"
                    InputProps={{ inputProps: { step: 0.1, min: 0 } }}
                    helperText="Optional: caps MFN + this authority to this total"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Ceiling Against</InputLabel>
                    <Select
                      value={rule.ceilingAgainstAuthority || 'MFN'}
                      onChange={(e) => updateRule(index, 'ceilingAgainstAuthority' as any, e.target.value)}
                      label="Ceiling Against"
                    >
                      <MenuItem value="MFN">MFN</MenuItem>
                      {AUTHORITIES.filter((a: any) => a !== 'MFN').map((auth: any) => (
                        <MenuItem key={auth} value={auth}>{auth}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Default MFN; only applies if ceiling is set</FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextInput
                    label="Exclusive Group"
                    type="text"
                    value={rule.exclusiveGroup || ''}
                    handleChangeValue={(value) => updateRule(index, 'exclusiveGroup', value)}
                    placeholder="e.g., china-exclusions"
                    helperText="Rules in same group compete based on mode"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Exclusive Mode</InputLabel>
                    <Select
                      value={rule.exclusiveMode || ''}
                      onChange={(e) => updateRule(index, 'exclusiveMode', e.target.value)}
                      label="Exclusive Mode"
                      disabled={!rule.exclusiveGroup}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value={ExclusiveMode.FIRST_WINS}>First Wins</MenuItem>
                      <MenuItem value={ExclusiveMode.MAX_RATE}>Max Rate</MenuItem>
                      <MenuItem value={ExclusiveMode.LAST_WINS}>Last Wins</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <AuthorityCheckboxGroup
                    value={rule.allowedStacksWith || []}
                    onChange={(value: any) => updateRule(index, 'allowedStacksWith', value)}
                    label="Allowed to Stack With"
                    options={AUTHORITIES}
                    helperText="Bidirectional stacking: both rules must allow each other"
                    disabled={false}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <MultiAutocomplete
                    value={rule.requiredWith || []}
                    onChange={(value: any) => updateRule(index, 'requiredWith', value)}
                    label="Required With"
                    placeholder="Select authorities"
                    options={AUTHORITIES}
                  />
                </Grid>

                {rule.isMetal && (
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: tokens.status.warningBg,
                      borderRadius: tokens.radius.md,
                      border: '1px solid #fcd34d'
                    }}>
                      <Typography variant="subtitle2" fontWeight={tokens.fontWeight.bold} sx={{ mb: 1, color: tokens.chip.amber.text }}>
                        Non-Metal Portion Stacking
                      </Typography>
                      <AuthorityCheckboxGroup
                        value={rule.nonMetalAllowedStacksWith || []}
                        onChange={(value: any) => updateRule(index, 'nonMetalAllowedStacksWith', value)}
                        label="Non-Metal Portion Can Stack With"
                        options={AUTHORITIES}
                        helperText="When metal percentage is less than 100%, these authorities can apply to the non-metal portion"
                        disabled={false}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default React.memo(DutyRuleFormSection); 